import axios from "axios";
import { uuid } from "@/lib/offlineQueue";
import { requestWorkspace } from "@/lib/operationalContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
if (!BACKEND_URL) throw new Error("REACT_APP_BACKEND_URL is required");

// One-time upgrade cleanup. Browser-readable bearer credentials are never used.
export function clearLegacyTokens() {
  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (const key of ["ck_token", "access_token", "refresh_token"]) {
      try { storage.removeItem(key); } catch (_) { /* storage may be disabled */ }
    }
  }
}
clearLegacyTokens();

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

let _redirectingOnAuthFail = false;
let refreshPromise = null;
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  Object.entries(requestWorkspace(config)).forEach(([key, value]) => { if (!config.headers[key]) config.headers[key] = value; });
  if (["post", "put", "patch", "delete"].includes(config.method) && !config.url.includes("/auth/")) {
    config.headers["Idempotency-Key"] ||= uuid();
  }
  return config;
});
export function redirectToLogin() {
  if (_redirectingOnAuthFail || ["/login", "/biller-login"].includes(window.location.pathname)) return;
  _redirectingOnAuthFail = true;
  localStorage.removeItem("ck_offline_identity");
  const returnTo = window.location.pathname + window.location.search;
  const loginPath = returnTo.startsWith("/pos") ? "/biller-login" : "/login";
  window.location.replace(`${loginPath}?returnTo=${encodeURIComponent(returnTo)}`);
}

export function refreshCookieSession() {
  if (!refreshPromise) {
    refreshPromise = api.post("/auth/refresh").finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const status = error?.response?.status;
    const path = error?.config?.url || "";
    const authEntry = /\/auth\/(login|biller\/login|logout|refresh)/.test(path);
    if (status === 401 && !authEntry && !path.includes("verify-manager-pin")) {
      if (!error.config._cookieRetry) {
        error.config._cookieRetry = true;
        try {
          await refreshCookieSession();
          return await api.request(error.config);
        } catch (refreshError) {
          if (!refreshError.response) return Promise.reject(refreshError);
        }
      }
      if (!path.endsWith("/auth/me")) redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export async function authenticatedFetch(url, options = {}) {
  const config = { ...options, credentials: "include", headers: {
    ...options.headers, "X-Requested-With": "XMLHttpRequest",
  }};
  let response = await fetch(url, config);
  if (response.status === 401) {
    try {
      await refreshCookieSession();
      response = await fetch(url, config);
    } catch (error) {
      if (error.response?.status === 401) redirectToLogin();
      throw error;
    }
    if (response.status === 401) redirectToLogin();
  }
  return response;
}

export { formatApiError } from "@/lib/apiErrors";

export const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// Trim floating-point noise from quantities (1.2000000000000013 → 1.2, -0.0299999999999997 → -0.03)
// Keeps up to 3 decimals; strips trailing zeros so "205.000" prints as "205".
export const qty = (n, decimals = 3) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0";
  return parseFloat(v.toFixed(decimals)).toString();
};

// Universal DD/MM/YYYY date formatter — India-standard.
// Accepts ISO string ("2026-02-27T…"), yyyy-mm-dd, Date, or timestamp.
// Returns "" when input is falsy so callers can safely chain.
export const fmtDate = (input) => {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
};

// DD/MM/YYYY HH:mm — for timestamps that need time context (bill printed at, audit rows).
export const fmtDateTime = (input) => {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const base = fmtDate(d);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${base} ${hh}:${mi}`;
};
